/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-02 12:07:54
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Field } from 'redux-form';
import {
  FormLayout,
  SkeletonDisplayText,
  Button,
  TextStyle,
  Link,
  Tooltip,
  Icon
} from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { ExternalSmallMinor, QuestionMarkMajor } from '@shopify/polaris-icons';
import { SEARCH_FIELDS_WEIGHT } from 'src/constants/search-settings';

import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { URL } from 'src/constants/links';
import { getLabelSearchField } from 'src/helpers/search-settings-helper';
import NoticeModal from 'src/components/views/common/notice-model';
import { store } from 'src/stores/store';

type Props = {
  fields: Object,
  loading: boolean,
  searchFields: Object,
  resetDefault: () => void,
  emailSupport: string
};

type ComposedProps = Props & WithI18nProps;

class SearchByOptions extends Component<ComposedProps> {
  state = {
    confirmMetafieldsModel: false
  };

  render() {
    const { i18n, loading, fields, searchFields, resetDefault, emailSupport } = this.props;
    const { confirmMetafieldsModel } = this.state;

    const searchFieldWeightOptions = SEARCH_FIELDS_WEIGHT.map(e => ({
      value: e.value,
      label: i18n.translate(e.label)
    }));
    return (
      <FormLayout>
        <div className="btn-reset">
          <Button plain size="slim" onClick={resetDefault}>
            {i18n.translate('AdminHTML.Common.Action.reset')}
          </Button>
        </div>
        <p>{i18n.translate('AdminHTML.SearchSettings.SearchFields.descriptionV2')}</p>
        <div
          className="Polaris-DataTable BC-DataTable__SearchSettings"
          style={{
            borderBottom: '0.0625rem solid var(--p-divider)'
          }}>
          <div className="Polaris-DataTable__ScrollContainer" style={{ marginLeft: 0 }}>
            <table className="Polaris-DataTable__Table">
              <thead>
                <tr>
                  <td className="Polaris-DataTable__Cell Polaris-DataTable__Cell-toggle"></td>
                  <td className="Polaris-DataTable__Cell Polaris-DataTable__Cell-searchField">
                    {i18n.translate('AdminHTML.SearchSettings.SearchFields.column01_Label')}

                    <span className="tooltip">
                      <Tooltip
                        content={
                          <span className="tooltipContent">
                            {i18n.translate(
                              'AdminHTML.SearchSettings.SearchFields.tooltipSearchFieldContent'
                            )}
                          </span>
                        }
                        preferredPosition="above"
                        light>
                        <Icon source={QuestionMarkMajor} color="base" />
                      </Tooltip>
                    </span>
                  </td>
                  <td className="Polaris-DataTable__Cell Polaris-DataTable__Cell-searchWeight">
                    {i18n.translate('AdminHTML.SearchSettings.SearchFields.column02_Label')}

                    <span className="tooltip">
                      <Tooltip
                        content={
                          <span className="tooltipContent">
                            {i18n.translate(
                              'AdminHTML.SearchSettings.SearchFields.tooltipSearchWeightContent'
                            )}
                          </span>
                        }
                        preferredPosition="above"
                        light>
                        <Icon source={QuestionMarkMajor} color="base" />
                      </Tooltip>
                    </span>
                  </td>
                </tr>
              </thead>
              {loading ? (
                <tbody>
                  {[...Array(5).keys()].map((item, key) => (
                    <tr key={key}>
                      <td className="Polaris-DataTable__Cell" key={key}>
                        <SkeletonDisplayText size="small" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              ) : !_.isEmpty(fields) ? (
                <tbody>
                  {fields.map((item, key) => {
                    const { name } = searchFields[key]?.name || 'name';
                    return (
                      <tr className="Polaris-DataTable__TableRow" key={`${name}-${key}`}>
                        <td className="Polaris-DataTable__Cell Polaris-DataTable__Cell-toggle">
                          <Field
                            name={`${item}.status`}
                            component={RenderToggleSwitch}
                            onChange={e => this.handleChange(e, key, 'status')}
                          />
                        </td>
                        <td className="Polaris-DataTable__Cell Polaris-DataTable__Cell-searchField">
                          {getLabelSearchField(searchFields, key)
                            ? i18n.translate(getLabelSearchField(searchFields, key))
                            : ''}
                        </td>
                        <td className="Polaris-DataTable__Cell Polaris-DataTable__Cell-searchWeight">
                          <Field
                            name={`${item}.searchWeight`}
                            options={searchFieldWeightOptions}
                            format={value => (_.isEmpty(value) ? 'non-searchable' : value)}
                            component={RenderSelect}
                            onChange={e => this.handleChange(e, key, 'weight')}
                          />
                        </td>
                        {searchFields[key]?.name === 'metafields' && (
                          <NoticeModal
                            active={confirmMetafieldsModel}
                            handleConfirm={this.handleCancelConfirm}
                            handleCancel={this.handleCancelConfirm}
                            title={i18n.translate(
                              'AdminHTML.SearchSettings.ConfirmEnableMetafieldsModal.title'
                            )}
                            content={i18n.translate(
                              'AdminHTML.SearchSettings.ConfirmEnableMetafieldsModal.description',
                              {
                                t1: (
                                  <Link external url={URL['Link_V2_Tool_Metafield_ManualGuide']}>
                                    {i18n.translate(
                                      'AdminHTML.SearchSettings.ConfirmEnableMetafieldsModal.content_t1'
                                    )}
                                  </Link>
                                )
                              }
                            )}
                          />
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              ) : (
                <tbody />
              )}
            </table>
          </div>
        </div>

        <p className="search-fields-footer">
          {i18n.translate('AdminHTML.SearchSettings.SearchFields.contentV2_Using', {
            t1: (
              <Link external url={URL['SearchByMetafieldDoc']}>
                {i18n.translate('AdminHTML.SearchSettings.SearchFields.contentV2_Using_t1')}
              </Link>
            ),

            icon: (
              <span className="external-link">
                <Button icon={ExternalSmallMinor} plain />
              </span>
            )
          })}
          <br />
          {i18n.translate('AdminHTML.SearchSettings.SearchFields.content_Contact', {
            nt_email: <TextStyle variation="strong">{emailSupport}</TextStyle>
          })}
        </p>
      </FormLayout>
    );
  }

  // Handle change status
  handleChange = (e, index, optionChange) => {
    const { handleUpdateIndex, searchFields } = this.props;

    // Handle metafields
    const isEnableMetafields =
      searchFields[index]?.name === 'metafields' &&
      optionChange === 'status' &&
      searchFields[index].status === false;

    if (isEnableMetafields) {
      const tenantApp = _.get(store.getState(), 'oauth.tenantApp', {});
      const metafieldIsAppliedToSearch = tenantApp?.parseParams?.metafields?.find(
        e => e.appliesTo?.search
      );

      if (!metafieldIsAppliedToSearch) {
        this.toggleModal('confirmMetafieldsModel');
      }
    }

    handleUpdateIndex(index, optionChange);
  };

  // Toggle modal
  toggleModal = (fieldName: string) => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  handleCancelConfirm = () => {
    this.toggleModal('confirmMetafieldsModel');
  };
}
export default withI18n()(SearchByOptions);
